/**
 * TODO DO THIS GENERAL ORDER OF PREFERENCE:
 * EFFICIENCY MODE:
 * 1. items without any existing relationships, efficiency: match against highest then lowest etc
 * 2. items from same rank, starting from highest rank
 * 3. least compared items
 *
 * FUN MODE would focus on 1. compare from similar rank 2. move around the ranks each time
 */

import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import './CompareForm.css';
import { ItemType, Relationship } from './types';

// TODO clean this up and write tests
const getTwoUncomparedItems = (
  items: Record<string, ItemType>,
  relationships: Record<string, Relationship>
) => {
  const itemsKeys = Object.keys(items);
  if (itemsKeys.length < 2) return;

  const twoRandomItems: ItemType[] = [];

  const theseTwoAlreadyCompared = () =>
    twoRandomItems.length >= 2 &&
    Object.values(relationships)
      .map((relationship) => relationship.options)
      .some((pair) =>
        twoRandomItems
          .map((item) => item.id)
          .every((item) => pair.includes(item))
      );

  const isValidPair = () =>
    twoRandomItems.length === 2 &&
    twoRandomItems[0].id !== twoRandomItems[1].id &&
    itemsKeys.includes(twoRandomItems[0].id) &&
    itemsKeys.includes(twoRandomItems[1].id);

  while (
    twoRandomItems.length < 2 ||
    theseTwoAlreadyCompared() ||
    !isValidPair()
  ) {
    twoRandomItems.splice(0, twoRandomItems.length);
    const firstItemsKeysKey = (itemsKeys.length * Math.random()) << 0;
    twoRandomItems.push(items[itemsKeys[firstItemsKeysKey]]);
    let secondItemsKeysKey = firstItemsKeysKey;
    while (secondItemsKeysKey === firstItemsKeysKey) {
      secondItemsKeysKey = (itemsKeys.length * Math.random()) << 0;
    }
    twoRandomItems.push(items[itemsKeys[secondItemsKeysKey]]);
  }

  return twoRandomItems;
};

/**
 * TODO this functionality need not be limited to only comparing 2 items.
 CompareItemsForm functionality+ could actually be merged with HiearchyDisplay functionality.
 addForCompare could add to a group of items that is displayed like HiearchyDisplay,
 and clicking on one of them would mark it as superior to all others in that collection.
 Could have arbitrarily deep sub-sections, branches, sub-groups of sub-groups.
 */
// TODO fix bug: do not allow comparison of item to self

const createRelationshipId = (options: string[], selection: string): string => {
  // Sort options to ensure consistent ordering
  const sortedOptions = [...options].sort();
  // Create a deterministic ID: "item1-item2-selection"
  return `${sortedOptions.join('-')}-${selection}`;
};

export default function CompareItemsForm({
  items,
  itemsToCompare,
  relationships,
  addRelationship,
  onItemSelect,
}: {
  items: Record<string, ItemType>;
  itemsToCompare: ItemType[];
  relationships: Record<string, Relationship>;
  addRelationship: (relationship: Relationship, onSuccess?: () => void) => void;
  onItemSelect?: (itemId: string) => void;
}) {
  const [twoItems, setTwoItems] = useState<ItemType[]>([]);

  const getTwoNewItems = () =>
    setTwoItems(getTwoUncomparedItems(items, relationships) || []);

  useEffect(() => {
    // Reset state when items change (e.g., when switching lists)
    setTwoItems([]);

    if (itemsToCompare && itemsToCompare.length === 2) {
      // Validate that both items exist in the current list
      if (items[itemsToCompare[0].id] && items[itemsToCompare[1].id]) {
        setTwoItems(itemsToCompare);
      } else {
        getTwoNewItems();
      }
    } else if (itemsToCompare && itemsToCompare.length === 1) {
      // Validate that the item exists in the current list
      if (items[itemsToCompare[0].id]) {
        if (twoItems && twoItems.length > 0 && items[twoItems[0].id]) {
          setTwoItems([itemsToCompare[0], twoItems[0]]);
        } else {
          getTwoNewItems();
          if (twoItems && twoItems.length > 0) {
            setTwoItems([itemsToCompare[0], twoItems[0]]);
          }
        }
      } else {
        getTwoNewItems();
      }
    } else {
      getTwoNewItems();
    }
  }, [items, itemsToCompare]);

  if (!twoItems || twoItems.length < 2 || !twoItems[0] || !twoItems[1]) {
    return <pre className="text-info">Requires 2 or more items</pre>;
  }

  const createRelationship = (selection: string) => {
    // Additional validation before creating relationship
    if (selection === twoItems[0].id && selection === twoItems[1].id) {
      console.warn('Cannot compare an item to itself');
      return;
    }
    if (!items[selection] || !items[twoItems[0].id] || !items[twoItems[1].id]) {
      console.warn('Cannot compare items from different lists');
      return;
    }

    const options = twoItems.map((item) => item.id);
    const relationship: Relationship = {
      id: createRelationshipId(options, selection),
      options,
      selection,
    };
    addRelationship(relationship, getTwoNewItems);
    onItemSelect?.(selection);
  };

  return (
    <>
      <Button
        className="mt-1 compare-form-button"
        variant="primary"
        onClick={() => createRelationship(twoItems[0].id)}
      >
        {twoItems[0].id}
      </Button>
      <Button
        className="mt-1 compare-form-button"
        variant="primary"
        onClick={() => createRelationship(twoItems[1].id)}
      >
        {twoItems[1].id}
      </Button>
      <Button
        className="mt-1 compare-form-button"
        variant="light"
        onClick={() => {
          getTwoNewItems();
        }}
      >
        <span className="text-muted">&#8811; skip &#8811;</span>
      </Button>
    </>
  );
}
